import { Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { AgBarSeriesOptions } from 'ag-charts-enterprise';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import { keyBy } from 'lodash';
import { useEffect } from 'react';

import RadialGaugeChart from '@/components/charts/RadialGaugeChart';
import VerticalBarChart from '@/components/charts/VerticalBarChart';
import { YEAR_MONTH_DAY_FORMAT } from '@/constants/date.constants';
import REPORT_PARAGRAPHS from '@/constants/reportparagraph.constants';
import ReportsTemplateBoxContainer from '@/features/ReportsTemplateBoxContainer/ReportsTemplateBoxContainer';
import ReportsTemplateCompareWidget from '@/features/ReportsTemplateCompareWidget/ReportsTemplateCompareWidget';
import ReportParagraph from '@/features/ReportsTemplateParagraph/ReportsParagraph';
import ReportsTemplateSectionTitle from '@/features/ReportsTemplateSectionTitle/ReportsTemplateSectionTitle';
import type {
  ServiceUsagePerDayDTO,
  UsageLimitType
} from '@/pages/Dashboard/Dashboard.types';
import { formatUsageGaugeText } from '@/pages/Dashboard/Dashboard.utils';

import {
  reportsEndDateAtom,
  reportsLogAvgAtom,
  reportsStartDateAtom,
  reportsTimeFrameAtom,
  reportsUsageDataAtom,
  reportsUsageLimitsDataAtom
} from '../../ReportsTemplate.hooks';
import useStyles from '../ReportsPages.styles';

const LogUsagePage = () => {
  const theme = useTheme();
  const { classes } = useStyles();
  const [usageData] = useAtom(reportsUsageDataAtom);
  const [usageLimitsData] = useAtom(reportsUsageLimitsDataAtom);
  const [timeframe] = useAtom(reportsTimeFrameAtom);
  const [startDate] = useAtom(reportsStartDateAtom);
  const [endDate] = useAtom(reportsEndDateAtom);
  const [, setLogAvg] = useAtom(reportsLogAvgAtom);

  // Min time frame is set to 7 days if the selected timeframe is less than 7 days
  // Only to be used for data over time graphs
  const minTimeFrame = timeframe < 7 ? 7 : timeframe;

  const logUsageLimit = usageLimitsData?.find(
    (item: UsageLimitType) => item.active && item.code === 'POND-LOG' // Checking against code because multiple products may have LOG product type
  )?.subscription_limit;

  // Used for log usage over time graph (includes 7 days of data)
  const logData = usageData?.filter(
    (row: ServiceUsagePerDayDTO) => row.type === 'log'
  );

  // Used for other graphs and widgets
  const logDataInTimeframe = logData?.filter((row: ServiceUsagePerDayDTO) => {
    const rowDate = dayjs(row.date);
    return (
      rowDate.isSame(dayjs(startDate), 'day') ||
      rowDate.isSame(dayjs(endDate), 'day') ||
      (rowDate.isAfter(dayjs(startDate), 'day') &&
        rowDate.isBefore(dayjs(endDate), 'day'))
    );
  });

  const total = Array.isArray(logDataInTimeframe)
    ? logDataInTimeframe.reduce(
        (acc: number, curr: ServiceUsagePerDayDTO) => acc + curr.gb,
        0
      )
    : 0;

  const logAverage = total / timeframe;

  const logUsageGraphData = () => {
    const logUsagePerDay = keyBy(logData, 'date');
    const usage = [];

    for (let i = 0; i <= minTimeFrame - 1; i += 1) {
      const date = `${dayjs(endDate)
        .subtract(minTimeFrame - 1 - i, 'days')
        .format(YEAR_MONTH_DAY_FORMAT)}`;

      const count = logUsagePerDay[date] ? logUsagePerDay[date]?.gb : 0;

      usage.push({
        date: dayjs(date).toDate(),
        usage: count,
        limit: logUsageLimit
      });
    }

    return usage;
  };

  const roundedLogAverage = Math.ceil(logAverage * 100) / 100;

  useEffect(() => {
    setLogAvg(roundedLogAverage);
  }, [roundedLogAverage]);

  const logIngestionPercent = logUsageLimit
    ? (roundedLogAverage / logUsageLimit) * 100
    : 0;

  const usageBarLineSeries = [
    {
      type: 'bar',
      xKey: 'date',
      yKey: 'usage',
      yName: 'Usage',
      itemStyler: (params: { datum: { usage: number; limit: number } }) => {
        let fill = theme.palette.info.main;
        if (params.datum.usage > params.datum.limit) {
          fill = theme.palette.warning.main;
        }
        return { fill };
      }
    },
    {
      type: 'line',
      xKey: 'date',
      yKey: 'limit',
      yName: 'License Limit',
      stroke: theme.palette.error.main,
      lineDash: [8, 8],
      marker: {
        enabled: false
      }
    }
  ];

  return (
    <div className={classes.reportsPageContainer}>
      <ReportsTemplateSectionTitle>LOG USAGE</ReportsTemplateSectionTitle>
      <ReportParagraph fontSize="12px">
        {REPORT_PARAGRAPHS.logUsageDescription}
      </ReportParagraph>
      <Stack sx={{ height: 250 }} direction="row" spacing={2}>
        <Box sx={{ width: '35%' }}>
          <ReportsTemplateBoxContainer height={117}>
            <ReportsTemplateCompareWidget
              title="Licensed Limit"
              value={`${logUsageLimit} GB/Day`}
            />
          </ReportsTemplateBoxContainer>
          <div style={{ marginTop: '16px' }}>
            <ReportsTemplateBoxContainer height={117}>
              <ReportsTemplateCompareWidget
                title="Daily Average"
                value={`${roundedLogAverage} GB/Day`}
              />
            </ReportsTemplateBoxContainer>
          </div>
        </Box>
        <Box
          data-testid="log-usage-radial-gauge"
          sx={{ maxHeight: 250, flexGrow: 1 }}
        >
          <RadialGaugeChart
            description={formatUsageGaugeText(
              timeframe,
              roundedLogAverage,
              logIngestionPercent,
              'log',
              logUsageLimit || 0
            )}
            limit={logUsageLimit || 0}
            orientation="vertical"
            percent={parseFloat(roundedLogAverage.toString())}
            primaryLabel={roundedLogAverage.toString()}
            secondaryLabel="GB/Day"
            sizeFontsForReports
            title={`Trailing ${timeframe}-day average`}
          />
        </Box>
      </Stack>
      <Box
        data-testid="log-usage-graph"
        sx={{ height: 300 }}
        className={classes.reportsChartContainer}
      >
        <Typography className={classes.reportsPreviewSubTitle}>
          {`Log Ingestion Over ${minTimeFrame} Days (GB/Day)`}
        </Typography>
        <VerticalBarChart
          data={logUsageGraphData()}
          series={usageBarLineSeries as AgBarSeriesOptions[]}
          suppressExponentialAxis
          suppressDoubleAxis
          suppressLegend
        />
      </Box>
      <ReportParagraph fontSize="10px">
        {REPORT_PARAGRAPHS.logUsageGraphDescription}
      </ReportParagraph>
    </div>
  );
};

export default LogUsagePage;
